import { OrganizationUnitStatus } from '@today/types'
import { EmptyState, FieldShell } from '@today/ui-components'
import { Badge, Button, Input } from '@today/ui-primitives'
import { type DragEvent, type KeyboardEvent, type ReactElement, useEffect, useRef, useState } from 'react'
import type { OrgLoadState, OrgTreeRowView, Translate } from './org-types'

export const ORG_STATUS_VARIANT: Record<OrganizationUnitStatus, 'success' | 'muted' | 'danger' | 'secondary'> = {
	[OrganizationUnitStatus.ACTIVE]: 'success',
	[OrganizationUnitStatus.INACTIVE]: 'muted',
	[OrganizationUnitStatus.CLOSED]: 'danger',
	[OrganizationUnitStatus.PLANNED]: 'secondary',
}

/**
 * Props for {@link OrgTreeView}.
 *
 * @property rows - Every flattened row (depth-first); the view renders `rows[window.start…window.end)`.
 * @property window - The slice to render and the spacer heights (computed by the caller from `scrollTop`).
 * @property scrollTop - Controlled scroll offset; the view reports user scrolls through `onScroll`.
 * @property matchCount - The number of search matches, or null when no query is active.
 * @property onNew - Present only when the viewer may create units.
 * @property onDrop - Present only when the viewer may move units: enables drag-and-drop and the root drop zone.
 * @property isLegalDrop - Whether dropping `unitId` onto `targetId` (null = root) is allowed (drag-over feedback).
 */
export interface OrgTreeViewProps {
	t: Translate
	state: OrgLoadState
	rows: OrgTreeRowView[]
	window: { start: number; end: number; padTop: number; padBottom: number }
	rowHeight: number
	viewportHeight: number
	scrollTop: number
	selectedId: string | null
	query: string
	matchCount: number | null
	onQueryChange: (query: string) => void
	onScroll: (scrollTop: number) => void
	onToggle: (id: string) => void
	onSelect: (id: string) => void
	onRetry: () => void
	onNew?: () => void
	onDrop?: (unitId: string, targetId: string | null) => void
	isLegalDrop?: (unitId: string, targetId: string | null) => boolean
}

const indexOf = (rows: OrgTreeRowView[], id: string | null) => (id === null ? -1 : rows.findIndex((r) => r.id === id))

/** The scroll offset that brings row `index` into the viewport, or null when it already is. */
const scrollFor = (index: number, props: OrgTreeViewProps): number | null => {
	const top = index * props.rowHeight
	if (top < props.scrollTop) return top
	if (top + props.rowHeight > props.scrollTop + props.viewportHeight)
		return top + props.rowHeight - props.viewportHeight
	return null
}

interface DragState {
	dragging: string | null
	setDragging: (id: string | null) => void
}

/** Drag-over/drop handlers for a drop target (`null` = root). */
const dropHandlers = (props: OrgTreeViewProps, drag: DragState, targetId: string | null) => ({
	onDragOver: (e: DragEvent) => {
		if (drag.dragging && props.isLegalDrop?.(drag.dragging, targetId)) e.preventDefault()
	},
	onDrop: (e: DragEvent) => {
		e.preventDefault()
		const unitId = drag.dragging ?? e.dataTransfer?.getData('text/plain')
		drag.setDragging(null)
		if (unitId) props.onDrop?.(unitId, targetId)
	},
})

/** A row's drag source + drop target props, only when the viewer may move units. */
const rowDragProps = (props: OrgTreeViewProps, drag: DragState, rowId: string) =>
	props.onDrop
		? {
				draggable: true,
				onDragStart: (e: DragEvent) => {
					e.dataTransfer?.setData('text/plain', rowId)
					drag.setDragging(rowId)
				},
				onDragEnd: () => drag.setDragging(null),
				...dropHandlers(props, drag, rowId),
			}
		: {}

function RowToggle({ props, row }: { props: OrgTreeViewProps; row: OrgTreeRowView }): ReactElement {
	if (!row.hasChildren) return <span className="w-4" aria-hidden="true" />
	return (
		<button
			type="button"
			tabIndex={-1}
			aria-label={props.t(row.expanded ? 'tree.collapse' : 'tree.expand')}
			className="w-4 text-ds-text-muted"
			onClick={(e) => {
				e.stopPropagation()
				props.onToggle(row.id)
			}}
		>
			{row.expanded ? '▾' : '▸'}
		</button>
	)
}

function TreeRow({
	props,
	row,
	focused,
	onFocusRow,
	drag,
}: {
	props: OrgTreeViewProps
	row: OrgTreeRowView
	focused: boolean
	onFocusRow: (id: string) => void
	drag: DragState
}): ReactElement {
	const { t } = props
	const selected = row.id === props.selectedId
	return (
		// biome-ignore lint/a11y/useKeyWithClickEvents: the tree container owns the keyboard (Up/Down/Left/Right/Enter) for every row (roving focus).
		<div
			role="treeitem"
			aria-level={row.depth + 1}
			aria-selected={selected}
			aria-expanded={row.hasChildren ? row.expanded : undefined}
			tabIndex={focused ? 0 : -1}
			data-unit-id={row.id}
			title={`${row.code} · ${row.name}`}
			style={{ height: props.rowHeight, paddingLeft: 8 + row.depth * 16 }}
			className={`flex cursor-pointer items-center gap-2 pr-2 text-ds-sm ${selected ? 'bg-ds-surface-muted font-semibold' : ''}`}
			onClick={() => {
				onFocusRow(row.id)
				props.onSelect(row.id)
			}}
			{...rowDragProps(props, drag, row.id)}
		>
			<RowToggle props={props} row={row} />
			<span className="font-mono">{row.code}</span>
			<span className="truncate">{row.name}</span>
			<span className="text-ds-xs text-ds-text-muted">{row.typeCode}</span>
			<Badge size="sm" variant={ORG_STATUS_VARIANT[row.status]}>
				{t(`status.${row.status}`)}
			</Badge>
			<span className="ml-auto text-ds-xs text-ds-text-muted">
				{t('tree.descendants', { count: row.descendantCount })}
			</span>
		</div>
	)
}

/** The tree's keyboard map for the focused row (roving focus). */
const keyActions = (
	props: OrgTreeViewProps,
	index: number,
	focusRow: (index: number) => void,
): Record<string, () => void> => {
	const row = props.rows[index]
	return {
		ArrowDown: () => focusRow(index + 1),
		ArrowUp: () => focusRow(index - 1),
		ArrowRight: () => row?.hasChildren && !row.expanded && props.onToggle(row.id),
		ArrowLeft: () => row?.hasChildren && row.expanded && props.onToggle(row.id),
		Enter: () => row && props.onSelect(row.id),
	}
}

/** A scroll container ref kept at the controlled `scrollTop`. */
function useControlledScroll(scrollTop: number) {
	const container = useRef<HTMLDivElement>(null)
	useEffect(() => {
		if (container.current && container.current.scrollTop !== scrollTop) container.current.scrollTop = scrollTop
	}, [scrollTop])
	return container
}

function TreeBody({ props }: { props: OrgTreeViewProps }): ReactElement {
	const { t, rows, window } = props
	const container = useControlledScroll(props.scrollTop)
	const [focusId, setFocusId] = useState<string | null>(null)
	const [dragging, setDragging] = useState<string | null>(null)
	const drag: DragState = { dragging, setDragging }
	const focused = focusId ?? props.selectedId ?? rows[0]?.id ?? null

	const focusRow = (index: number) => {
		const row = rows[index]
		if (!row) return
		setFocusId(row.id)
		const next = scrollFor(index, props)
		if (next !== null) props.onScroll(next)
	}

	const onKeyDown = (e: KeyboardEvent) => {
		const action = keyActions(props, indexOf(rows, focused), focusRow)[e.key]
		if (action) {
			e.preventDefault()
			action()
		}
	}

	return (
		<div
			ref={container}
			role="tree"
			aria-label={t('tree.label')}
			tabIndex={-1}
			onKeyDown={onKeyDown}
			onScroll={(e) => props.onScroll(e.currentTarget.scrollTop)}
			style={{ height: props.viewportHeight }}
			className="overflow-y-auto rounded-ds-md border border-ds-border"
		>
			<div style={{ height: window.padTop }} aria-hidden="true" />
			{rows.slice(window.start, window.end).map((row) => (
				<TreeRow
					key={row.id}
					props={props}
					row={row}
					focused={row.id === focused}
					onFocusRow={setFocusId}
					drag={drag}
				/>
			))}
			<div style={{ height: window.padBottom }} aria-hidden="true" />
			{props.onDrop ? <RootDropZone props={props} drag={drag} /> : null}
		</div>
	)
}

function RootDropZone({ props, drag }: { props: OrgTreeViewProps; drag: DragState }): ReactElement {
	return (
		<div
			data-testid="org-root-drop-zone"
			className="sticky bottom-0 border-t border-dashed border-ds-border bg-ds-surface p-2 text-center text-ds-xs text-ds-text-muted"
			{...dropHandlers(props, drag, null)}
		>
			{props.t('tree.rootDropZone')}
		</div>
	)
}

function TreeContent({ props }: { props: OrgTreeViewProps }): ReactElement {
	const { t } = props
	if (props.state === 'loading') return <p aria-live="polite">{t('tree.loading')}</p>
	if (props.state === 'error') {
		return <EmptyState title={t('tree.error')} action={{ label: t('tree.retry'), onPress: props.onRetry }} />
	}
	if (props.state === 'empty') return <EmptyState title={t('tree.empty')} />
	if (props.matchCount === 0) return <EmptyState title={t('tree.noResults')} />
	return <TreeBody props={props} />
}

/** The organization tree: search, virtualized keyboard-operable rows, drag-to-reparent (organism). */
export function OrgTreeView(props: OrgTreeViewProps): ReactElement {
	const { t } = props
	return (
		<section className="flex flex-col gap-3">
			<div className="flex items-end gap-2">
				<FieldShell label={t('tree.search')} className="flex-1">
					<Input type="search" value={props.query} onChange={(e) => props.onQueryChange(e.target.value)} />
				</FieldShell>
				{props.onNew ? <Button onPress={props.onNew}>{t('tree.newUnit')}</Button> : null}
			</div>
			{props.matchCount !== null ? (
				<p className="text-ds-xs text-ds-text-muted" aria-live="polite">
					{t('tree.matches', { count: props.matchCount })}
				</p>
			) : null}
			<TreeContent props={props} />
		</section>
	)
}
